// Recebimentos de um negócio, num só olhar:
// 1) três cartões de resumo (valor de venda, agência, consultor) com barra de progresso;
// 2) Recebimento da agência: o cliente pagou o reporting (uma linha por lado), com linha cinzenta do que falta;
// 3) Recebimento do consultor: o consultor recebeu a sua percentagem, com linha cinzenta do que falta.
// As contas são automáticas: cada linha cinzenta traz o valor em falta e um botão "Receber" que abre a linha já preenchida.
// Cada consultor só vê a sua parte; a líder vê tudo. A outra agência (partilha externa) só aparece no quadro final.
import { useEffect, useRef, useState } from "react";
import { Wallet, Check, Pencil, Trash2, AlertTriangle, Landmark, Clock } from "lucide-react";
import { ds } from "./desenho";
import { paleta } from "./tokens";
import { minhas, faltaAgencia, r2 } from "./ComRec";
import { FASE_CONCLUIDO } from "./Contas";
import { eurTxt, maximoAgente, funcaoDe, validarRecebimento, repartirCliente, comissaoRecebida, modoDeRecebimento } from "./RecebimentoCalculo";

const GRADIENTE = "linear-gradient(90deg, #0B1B3D 0%, #1E3566 45%, #4A6BA8 100%)";
const num = (v) => Number(v || 0);
const dataPT = (d) => (d ? new Date(String(d).slice(0, 10) + "T12:00:00").toLocaleDateString("pt-PT") : "");
const mesPT = (d) => (d ? new Date(String(d).slice(0, 10) + "T12:00:00").toLocaleDateString("pt-PT", { month: "2-digit", year: "2-digit" }) : "");
const hojeISO = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
const NOME_EVENTO = { cpcv: "CPCV", escritura: "Escritura", parcial: "Parcial", outro: "Outro" };

const caixa = { background: "#fff", border: "1px solid " + paleta.cinza200, borderRadius: 12, overflow: "hidden" };
const barra = { background: GRADIENTE, color: "#fff", padding: "10px 16px", fontSize: 14.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const campo = { fontSize: 13, border: "1px solid #D5DDEE", borderRadius: 8, padding: "6px 8px", width: "100%", fontFamily: "inherit", background: "#fff", boxSizing: "border-box" };
const btnBase = { borderRadius: 8, padding: "8px 14px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" };
const btnPrincipal = { ...btnBase, background: "#1E3566", color: "#fff", border: "1px solid #1E3566" };
const btnSecundario = { ...btnBase, background: "#fff", color: paleta.navy900, border: "1px solid " + paleta.cinza200 };
const btnPequeno = { ...btnSecundario, padding: "4px 10px", fontSize: 12.5 };
const btnIcone = { ...btnSecundario, padding: "5px 8px" };
const erroTxt = { color: ds.falta, fontSize: 12.5, fontWeight: 600 };
const cinzaLinha = { background: "#F1F2F4", color: "#6B7385" };

function Progresso({ pago, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((pago / total) * 100)) : 0;
  return <div style={{ height: 6, borderRadius: 999, background: "#E8ECF4", overflow: "hidden" }}><div style={{ height: "100%", width: pct + "%", background: pct >= 100 ? ds.ok : "#1E3566", borderRadius: 999 }} /></div>;
}

// Cartão de resumo: valor grande, nota do que falta e barra fina de progresso
function Cartao({ titulo, grande, de, nota, notaCor, pago, total, barraVisivel = true }) {
  return (
    <div style={{ ...caixa, padding: "12px 16px", display: "grid", gap: 6, alignContent: "start" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: ds.tinta2, textTransform: "uppercase", letterSpacing: "0.04em" }}>{titulo}</div>
      <div><span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{grande}</span>{de && <span style={{ fontSize: 13, color: ds.tinta2 }}> {de}</span>}</div>
      {nota && <div style={{ fontSize: 13, fontWeight: 600, color: notaCor || ds.tinta2 }}>{nota}</div>}
      {barraVisivel && <Progresso pago={pago} total={total} />}
    </div>
  );
}

function CabecalhoSecao({ titulo, explica, children }) {
  return (
    <div style={barra}>
      <Wallet size={16} aria-hidden="true" />{titulo}
      <span style={{ fontSize: 12.5, fontWeight: 400, opacity: 0.9 }}>{explica}</span>
      <span style={{ marginLeft: "auto" }}>{children}</span>
    </div>
  );
}

// Quadro geral do negócio, só informativo. Vai no fim da página.
export function QuadroFinal({ neg, nc, isLider, userId, onEditarComissao }) {
  const internas = nc ? nc.internas || [] : [];
  const todas = nc ? nc.linhas || [] : [];
  const minhasL = nc ? minhas(nc, isLider ? [] : [userId]) : [];
  const partes = isLider ? todas : minhasL;
  const outros = todas.filter((l) => !partes.includes(l));
  const falta = !nc || !(num(nc.total) > 0) || !internas.length || internas.some((o) => !(num(o.c) > 0));
  const cab = isLider ? ["Parte", "Função", "Reporting", "Percentagem", "Comissão"] : ["Consultor", "Função", "Reporting", "Percentagem", "Comissão"];
  return (
    <div style={{ background: "#F1F2F4", border: "1px solid #E2E4E9", borderRadius: 12, padding: "12px 16px", color: "#6B7385", display: "grid", gap: 10 }} aria-label="Quadro geral do negócio">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Quadro geral do negócio, só informativo</span>
        {isLider && onEditarComissao && <button onClick={onEditarComissao} style={{ background: "none", border: 0, color: "#4A5778", fontWeight: 600, fontSize: 13, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>Editar comissão</button>}
      </div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div><div style={{ fontSize: 12, color: "#8A909D" }}>Valor de venda</div><div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{num(neg.valor_venda) > 0 ? eurTxt(neg.valor_venda) : "por definir"}</div></div>
        {isLider && <div><div style={{ fontSize: 12, color: "#8A909D" }}>Comissão total</div><div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{nc && num(nc.total) > 0 ? eurTxt(nc.total) : "por definir"}</div></div>}
        {!isLider && outros.length > 0 && <div><div style={{ fontSize: 12, color: "#8A909D" }}>Partilha</div><div style={{ fontSize: 16, fontWeight: 600 }}>Partilhado com {outros.map((l) => l.nome || "outra agência").join(", ")}</div></div>}
      </div>
      {partes.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 520 }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(140px,1.4fr) 110px 110px 110px 110px", gap: 8, fontSize: 12, fontWeight: 700, padding: "4px 0", borderBottom: "1px solid #E2E4E9" }}>{cab.map((h) => <div key={h}>{h}</div>)}</div>
            {partes.map((l) => (
              <div key={l.k} style={{ display: "grid", gridTemplateColumns: "minmax(140px,1.4fr) 110px 110px 110px 110px", gap: 8, fontSize: 13.5, padding: "6px 0", borderBottom: "1px solid #E8EAEE", fontVariantNumeric: "tabular-nums" }}>
                <div style={{ fontWeight: 600 }}>{l.nome || "sem nome"}{l.externo ? " (outra agência)" : ""}</div>
                <div>{funcaoDe(l)}</div>
                <div>{num(l.r) > 0 ? eurTxt(l.r) : "sem valor"}</div>
                <div>{l.externo ? "não aplicável" : l.pct != null ? String(l.pct).replace(".", ",") + "%" : "por definir"}</div>
                <div>{l.externo ? "não aplicável" : num(l.c) > 0 ? eurTxt(l.c) : "por definir"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isLider && falta && <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12.5, color: ds.atencao }}><AlertTriangle size={14} aria-hidden="true" />Falta definir valores na comissão. Sem eles não é possível registar recebimentos.</div>}
    </div>
  );
}

function JanelaConcluido({ neg, onEnviar, onFechar }) {
  return (
    <div role="dialog" aria-modal="true" aria-label="Negócio concluído" onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(10,18,40,.45)", zIndex: 9500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: 460, maxWidth: "100%", overflow: "hidden", fontFamily: ds.letra, color: ds.tinta }}>
        <div style={{ ...barra, fontSize: 16, fontWeight: 700, padding: "14px 20px" }}><Check size={18} aria-hidden="true" />Negócio concluído</div>
        <div style={{ padding: 20, display: "grid", gap: 14 }}>
          <span style={{ fontSize: 14.5 }}>A escritura está realizada e a comissão da agência e dos consultores está toda recebida{neg.imovel_ref ? " (" + neg.imovel_ref + ")" : ""}. Quer enviar agora o formulário de satisfação aos clientes?</span>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button onClick={onFechar} style={btnSecundario}>Mais tarde</button>
            <button onClick={onEnviar} style={btnPrincipal}>Enviar formulário agora</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const G_AG = "78px 92px minmax(110px,1fr) 96px 92px 96px 96px 150px 96px";
const G_CONS = "78px minmax(110px,1fr) 110px minmax(110px,1fr) 150px 96px";
const CAB_AG = ["Data", "Evento", "Consultor", "Valor", "Mês do reporting", "Nº fatura", "Nº recibo", "Estado e confirmação", "Ações"];
const CAB_CONS = ["Data", "Consultor", "Valor recebido", "Nº fatura/recibo", "Confirmado", "Ações"];

function CabTabela({ grid, cab }) {
  return <div style={{ display: "grid", gridTemplateColumns: grid, gap: 8, padding: "9px 16px", background: ds.navy0, fontSize: 12, fontWeight: 700, color: ds.tinta2 }}>{cab.map((h) => <div key={h}>{h}</div>)}</div>;
}

export default function RecebimentosNegocio({ neg, nc, recs = [], formulariosSatisfacao = [], supabase, perfilId, nomeUtilizador, isLider = false, userId = null, profile = null, onRecarregar, onEditar, onAviso, onEnviarForm, onAbrirNegocio, autoOpen = false, extraLinha = null, referencia = null }) {
  // referencia: { direcao } quando a janela é usada numa referência (sem escritura nem conclusão); extraLinha: campos acrescentados a cada linha gravada
  const linhas = nc ? minhas(nc, isLider ? [] : [userId]) : [];
  const partilhado = linhas.length > 1;
  const totalR = r2(linhas.reduce((s, o) => s + num(o.r), 0));
  const totalRec = r2(linhas.reduce((s, o) => s + num(o.rec), 0));
  const totalC = r2(linhas.reduce((s, o) => s + num(o.c), 0));
  const totalCRec = r2(linhas.reduce((s, o) => s + num(o.cRec), 0));

  // Concluído: escritura realizada e tudo recebido (agência e consultores)
  const escrituraFeita = !!neg.data_escritura_realizada || FASE_CONCLUIDO.includes(neg.estado);
  const tudoRecebido = nc ? comissaoRecebida(nc.internas || []) : false;
  const concluido = !referencia && escrituraFeita && tudoRecebido;
  const satisfacaoEnviada = (formulariosSatisfacao || []).some((f) => f.negocio_id === neg.id && (f.enviado_em || f.preenchido_em));
  const antesConcluido = useRef(concluido);
  const [avisoConcluido, setAvisoConcluido] = useState(false);
  useEffect(() => {
    if (concluido && !antesConcluido.current) setAvisoConcluido(true);
    antesConcluido.current = concluido;
  }, [concluido]);

  const faseEvento = referencia ? "outro" : nc && nc.fase === "realizada" ? "escritura" : "cpcv";
  const mesAtual = hojeISO().slice(0, 7);
  const [ed, setEd] = useState(null); // linha aberta para receber: { tab, k, valor, data, evento, mes, fatura, recibo, erro }
  const [tot, setTot] = useState(null); // "cliente pagou o total": { valor, data, evento, mes, fatura, recibo, erro }
  const [aAnular, setAAnular] = useState(null);
  const [aGravar, setAGravar] = useState(false);
  const [editMes, setEditMes] = useState(null); // { id, mes, erro }: edição do mês de reporting numa linha

  const abrirAg = (o) => setEd({ tab: "ag", k: o.k, valor: String(faltaAgencia(o)), data: hojeISO(), evento: faseEvento, mes: mesAtual, fatura: "", recibo: "", erro: "" });
  const abrirCons = (o) => setEd({ tab: "cons", k: o.k, valor: String(maximoAgente(o, 0)), data: hojeISO(), evento: faseEvento, mes: "", fatura: "", recibo: "", erro: "" });
  const mudarEd = (chave, v) => setEd((p) => ({ ...p, [chave]: v, ...(chave === "erro" ? {} : { erro: "" }) }));
  const mudarTot = (chave, v) => setTot((p) => ({ ...p, [chave]: v, ...(chave === "erro" ? {} : { erro: "" }) }));

  const jaAbriu = useRef(false);
  useEffect(() => {
    if (!autoOpen || jaAbriu.current) return;
    const primeira = linhas.find((o) => faltaAgencia(o) > 0.01);
    if (primeira) { jaAbriu.current = true; abrirAg(primeira); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  const confirmacao = () => ({ confirmado_por_outro_id: perfilId || null, confirmado_por_outro_em: new Date().toISOString(), confirmado_por_outro_nome: nomeUtilizador || (profile && profile.nome) || null });
  const base = (o) => ({ comissao_id: nc.comissao ? nc.comissao.id : null, negocio_id: neg.id, consultor_id: o.id || null, consultor_nome: o.nome, criado_por: perfilId || null, ...confirmacao(), ...(extraLinha || {}) });
  const linhaAgencia = (o, valor, v) => ({
    ...base(o), tipo_evento: v.evento, data_evento: v.data || null, numero_fatura: (v.fatura || "").trim() || null, numero_recibo: (v.recibo || "").trim() || null,
    tipo_recebimento: modoDeRecebimento(valor, 0), valor_cliente: valor, data_pago_cliente: v.data || null, valor_consultor: 0, data_pago_consultor: null,
    reporting_gerado: valor, reportado: !!v.mes, data_reportado: v.mes ? v.mes + "-01" : null, notas_reporting: null,
  });

  const notificar = async (gravadas) => {
    try {
      for (const l of gravadas) {
        if (!(l.valor_cliente > 0) || !l.consultor_id || l.consultor_id === perfilId) continue;
        await supabase.from("notificacoes").insert({ user_id: l.consultor_id, remetente_id: perfilId || null, remetente_nome: nomeUtilizador || "Sistema", tipo: "recebimento_partilhado", negocio_id: extraLinha && "negocio_id" in extraLinha ? extraLinha.negocio_id : neg.id, lida: false,
          mensagem: "Foi registado o pagamento do cliente de " + eurTxt(l.valor_cliente) + " da tua parte no negócio " + (neg.imovel_ref || "") + "." });
      }
    } catch (_) { /* o aviso é secundário: o recebimento já ficou gravado */ }
  };

  const inserir = async (dados, mensagem, onErro) => {
    setAGravar(true);
    const { error } = await supabase.from("recebimentos_comissao").insert(dados);
    if (error) { setAGravar(false); onErro("Não foi possível gravar: " + error.message); return false; }
    await notificar(dados);
    await onRecarregar();
    setAGravar(false);
    if (onAviso) onAviso(mensagem);
    return true;
  };

  const confirmarLinha = async () => {
    const o = linhas.find((x) => x.k === ed.k);
    if (!o) return;
    const valor = num(ed.valor);
    if (!(valor > 0)) return mudarEd("erro", "Indique o valor recebido.");
    const agencia = ed.tab === "ag";
    const { erros } = validarRecebimento([o], { [o.k]: agencia ? { ag: valor, dAg: ed.data } : { agente: valor, dAgente: ed.data } });
    const e = erros[o.k];
    if (e) return mudarEd("erro", Object.values(e).join(" "));
    let dados;
    if (agencia) dados = linhaAgencia(o, valor, ed);
    else dados = { ...base(o), tipo_evento: faseEvento, data_evento: ed.data || null, numero_fatura: ed.fatura.trim() || null, numero_recibo: null,
      tipo_recebimento: modoDeRecebimento(0, valor), valor_cliente: 0, data_pago_cliente: null, valor_consultor: valor, data_pago_consultor: ed.data || null,
      reporting_gerado: 0, reportado: false, data_reportado: null, notas_reporting: null };
    const ok = await inserir([dados], agencia ? "Recebimento da agência registado." : "Recebimento do consultor registado.", (m) => mudarEd("erro", m));
    if (ok) setEd(null);
  };

  const abrirTotal = () => setTot({ valor: "", data: hojeISO(), evento: faseEvento, mes: mesAtual, fatura: "", recibo: "", erro: "" });
  const parteTotal = tot ? repartirCliente(num(tot.valor), linhas) : {};
  const faltaTotal = r2(linhas.reduce((s, o) => s + faltaAgencia(o), 0));
  const confirmarTotal = async () => {
    const valor = num(tot.valor);
    if (!(valor > 0)) return mudarTot("erro", "Indique quanto o cliente pagou.");
    if (valor > faltaTotal + 0.005) return mudarTot("erro", "Só faltam " + eurTxt(faltaTotal) + " à agência neste negócio.");
    if (!tot.data) return mudarTot("erro", "Indique a data em que o cliente pagou.");
    const dados = linhas.filter((o) => num(parteTotal[o.k]) > 0).map((o) => linhaAgencia(o, parteTotal[o.k], tot));
    const ok = await inserir(dados, "Recebimento da agência registado.", (m) => mudarTot("erro", m));
    if (ok) setTot(null);
  };

  const anular = async (r) => {
    const { error } = await supabase.from("recebimentos_comissao").update({ deleted_at: new Date().toISOString() }).eq("id", r.id);
    if (error) { if (onAviso) onAviso("Não foi possível anular: " + error.message, "error"); return; }
    await onRecarregar();
    if (onAviso) onAviso("Recebimento anulado.");
  };
  const confirmar = async (r) => {
    const { error } = await supabase.from("recebimentos_comissao").update(confirmacao()).eq("id", r.id);
    if (error) { if (onAviso) onAviso("Erro: " + error.message, "error"); return; }
    await onRecarregar();
    if (onAviso) onAviso("Recebimento confirmado.");
  };
  const marcarNC = async (r) => {
    const { error } = await supabase.from("recebimentos_comissao").update({ nota_credito_estado: "emitida", nota_credito_emitida_em: new Date().toISOString() }).eq("id", r.id);
    if (error) { if (onAviso) onAviso("Erro: " + error.message, "error"); return; }
    await onRecarregar();
    if (onAviso) onAviso("Nota de crédito marcada como emitida.");
  };

  if (!nc || !(nc.internas || []).length) {
    return <div style={{ ...caixa, padding: 20, color: ds.tinta2, fontSize: 14 }}>Este negócio ainda não tem a comissão distribuída pelos consultores. Edite a comissão para poder registar recebimentos.</div>;
  }

  const ordem = (a, b) => String(a.data_pago_cliente || a.data_pago_consultor || a.data_evento || a.created_at || "").localeCompare(String(b.data_pago_cliente || b.data_pago_consultor || b.data_evento || b.created_at || ""));
  const meu = (r) => isLider || (r.consultor_id && r.consultor_id === userId);
  const histAg = recs.filter((r) => num(r.valor_cliente) > 0 && meu(r)).sort(ordem);
  const histCons = recs.filter((r) => num(r.valor_consultor) > 0 && meu(r)).sort(ordem);

  const podeConfirmar = (r) => !r.confirmado_por_outro_em && (isLider || (r.consultor_id && r.consultor_id === userId));
  const celConfirmado = (r) => (r.confirmado_por_outro_em
    ? <span style={{ fontSize: 12.5, color: ds.ok, fontWeight: 600 }}>confirmado por {r.confirmado_por_outro_nome || ""}<span style={{ color: ds.tinta2, fontWeight: 400 }}> {dataPT(r.confirmado_por_outro_em)}</span></span>
    : podeConfirmar(r) ? <button onClick={() => confirmar(r)} aria-label="Confirmar este recebimento" style={btnPequeno}>Confirmar</button> : <span style={{ fontSize: 12.5, color: ds.tinta3 }}>por confirmar</span>);

  // Mês de reporting: clicável para definir ou mudar (vazio = volta a "por reportar")
  const guardarMes = async () => {
    const m = editMes.mes;
    const { error } = await supabase.from("recebimentos_comissao").update({ reportado: !!m, data_reportado: m ? m + "-01" : null }).eq("id", editMes.id);
    if (error) { setEditMes((p) => ({ ...p, erro: "Não foi possível guardar: " + error.message })); return; }
    setEditMes(null);
    await onRecarregar();
    if (onAviso) onAviso(m ? "Mês de reporting guardado." : "Mês de reporting retirado.");
  };
  const celMes = (r) => {
    if (editMes && editMes.id === r.id) {
      return (
        <div style={{ display: "grid", gap: 4 }}>
          <input type="month" aria-label="Mês do reporting" value={editMes.mes} onChange={(e) => setEditMes((p) => ({ ...p, mes: e.target.value, erro: "" }))} style={campo} />
          <span style={{ display: "flex", gap: 4 }}>
            <button onClick={guardarMes} aria-label="Guardar o mês de reporting" style={btnPequeno}>Guardar</button>
            <button onClick={() => setEditMes(null)} style={btnPequeno}>Cancelar</button>
          </span>
          {editMes.erro && <span role="alert" style={{ ...erroTxt, fontSize: 12 }}>{editMes.erro}</span>}
        </div>
      );
    }
    const texto = r.reportado && r.data_reportado ? mesPT(r.data_reportado) : "por reportar";
    if (!meu(r)) return <span style={{ color: r.reportado ? ds.tinta : ds.tinta3 }}>{texto}</span>;
    return (
      <button onClick={() => setEditMes({ id: r.id, mes: r.reportado && r.data_reportado ? String(r.data_reportado).slice(0, 7) : mesAtual, erro: "" })}
        title={r.reportado ? "Mudar o mês de reporting" : "Definir o mês de reporting"} aria-label={(r.reportado ? "Mudar o mês de reporting: " : "Definir o mês de reporting: ") + texto}
        style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", textDecoration: "underline dashed", textUnderlineOffset: 3, color: r.reportado ? ds.tinta : ds.tinta3 }}>{texto}</button>
    );
  };
  const acoes = (r, dupla) => (
    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
      {aAnular !== r.id && <>
        <button onClick={() => onEditar(r)} title="Editar este recebimento" aria-label="Editar este recebimento" style={btnIcone}><Pencil size={14} aria-hidden="true" /></button>
        <button onClick={() => setAAnular(r.id)} title="Anular este recebimento" aria-label="Anular este recebimento" style={{ ...btnIcone, color: ds.falta }}><Trash2 size={14} aria-hidden="true" /></button>
      </>}
      {aAnular === r.id && <span style={{ display: "inline-flex", gap: 4, alignItems: "center", fontSize: 12.5, flexWrap: "wrap" }}>{dupla ? "Anular os dois valores?" : "Anular?"}
        <button onClick={() => { setAAnular(null); anular(r); }} style={{ ...btnPequeno, color: ds.falta, borderColor: ds.falta }}>Sim</button>
        <button onClick={() => setAAnular(null)} style={btnPequeno}>Não</button></span>}
    </div>
  );
  const nomeEvento = (e) => NOME_EVENTO[e] || e || "Outro";
  const chip = (txt, cinza) => <span style={{ background: cinza ? "#E2E4E9" : ds.navy1, color: cinza ? "#6B7385" : ds.navy9, borderRadius: 999, padding: "2px 9px", fontSize: 12, fontWeight: 600 }}>{txt}</span>;
  const linhaBase = { display: "grid", gap: 8, padding: "10px 16px", borderTop: "1px solid " + ds.linha, alignItems: "center", fontSize: 13.5 };
  const caixaErro = (m) => <div role="alert" style={{ ...erroTxt, padding: "6px 16px 10px", background: ds.faltaFundo, display: "flex", gap: 6, alignItems: "center" }}><AlertTriangle size={14} aria-hidden="true" />{m}</div>;

  // Linha cinzenta (a receber) e linha aberta para registar
  const linhaPendenteAg = (o) => {
    const falta = faltaAgencia(o);
    if (ed && ed.tab === "ag" && ed.k === o.k) {
      return (
        <div key={"p" + o.k} style={{ background: "#F4F7FD" }}>
          <div style={{ ...linhaBase, gridTemplateColumns: G_AG }}>
            <input type="date" aria-label="Data do pagamento" value={ed.data} onChange={(e) => mudarEd("data", e.target.value)} style={campo} />
            <select aria-label="Evento" value={ed.evento} onChange={(e) => mudarEd("evento", e.target.value)} style={campo}>{Object.entries(NOME_EVENTO).map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select>
            <div style={{ fontWeight: 600 }}>{o.nome}<div style={{ fontSize: 12, fontWeight: 400, color: ds.tinta2 }}>{funcaoDe(o)}</div></div>
            <input type="number" min="0" step="0.01" aria-label="Valor recebido" value={ed.valor} onChange={(e) => mudarEd("valor", e.target.value)} style={campo} />
            <input type="month" aria-label="Mês do reporting" value={ed.mes} onChange={(e) => mudarEd("mes", e.target.value)} style={campo} />
            <input type="text" aria-label="Nº da fatura" value={ed.fatura} placeholder="FT 2026/001" onChange={(e) => mudarEd("fatura", e.target.value)} style={campo} />
            <input type="text" aria-label="Nº do recibo" value={ed.recibo} onChange={(e) => mudarEd("recibo", e.target.value)} style={campo} />
            <div style={{ fontSize: 12.5, color: ds.tinta2 }}>falta {eurTxt(falta)}</div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={confirmarLinha} disabled={aGravar} style={{ ...btnPrincipal, padding: "6px 12px", opacity: aGravar ? 0.7 : 1 }}>{aGravar ? "A gravar..." : "Confirmar"}</button>
              <button onClick={() => setEd(null)} style={btnPequeno}>Cancelar</button>
            </div>
          </div>
          {ed.erro && caixaErro(ed.erro)}
        </div>
      );
    }
    return (
      <div key={"p" + o.k} style={{ ...linhaBase, ...cinzaLinha, gridTemplateColumns: G_AG }}>
        <div>—</div>
        <div>{chip(nomeEvento(faseEvento), true)}</div>
        <div style={{ fontWeight: 600 }}>{o.nome}</div>
        <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>falta {eurTxt(falta)}</div>
        <div style={{ gridColumn: "5 / 9", display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}><Clock size={14} aria-hidden="true" />A receber do cliente ({funcaoDe(o).toLowerCase()}, reporting de {eurTxt(o.r)})</div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}><button onClick={() => abrirAg(o)} aria-label={"Receber da agência a parte de " + o.nome} style={{ ...btnPrincipal, padding: "5px 12px" }}><Wallet size={14} aria-hidden="true" />Receber</button></div>
      </div>
    );
  };

  const linhaPendenteCons = (o) => {
    const falta = r2(num(o.c) - num(o.cRec));
    const max = maximoAgente(o, 0);
    const espera = r2(falta - max);
    if (ed && ed.tab === "cons" && ed.k === o.k) {
      return (
        <div key={"c" + o.k} style={{ background: "#F4F7FD" }}>
          <div style={{ ...linhaBase, gridTemplateColumns: G_CONS }}>
            <input type="date" aria-label="Data do recebimento" value={ed.data} onChange={(e) => mudarEd("data", e.target.value)} style={campo} />
            <div style={{ fontWeight: 600 }}>{o.nome}</div>
            <input type="number" min="0" step="0.01" aria-label="Valor recebido" value={ed.valor} onChange={(e) => mudarEd("valor", e.target.value)} style={campo} />
            <input type="text" aria-label="Nº da fatura ou recibo" value={ed.fatura} placeholder="FT ou recibo" onChange={(e) => mudarEd("fatura", e.target.value)} style={campo} />
            <div style={{ fontSize: 12.5, color: ds.tinta2 }}>pode receber agora {eurTxt(max)}</div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={confirmarLinha} disabled={aGravar} style={{ ...btnPrincipal, padding: "6px 12px", opacity: aGravar ? 0.7 : 1 }}>{aGravar ? "A gravar..." : "Confirmar"}</button>
              <button onClick={() => setEd(null)} style={btnPequeno}>Cancelar</button>
            </div>
          </div>
          {ed.erro && caixaErro(ed.erro)}
        </div>
      );
    }
    return (
      <div key={"c" + o.k} style={{ ...linhaBase, ...cinzaLinha, gridTemplateColumns: G_CONS }}>
        <div>—</div>
        <div style={{ fontWeight: 600 }}>{o.nome}</div>
        <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>falta {eurTxt(falta)}</div>
        <div style={{ gridColumn: "4 / 6", display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}><Clock size={14} aria-hidden="true" />{max > 0.01 ? "pode receber agora " + eurTxt(max) + (espera > 0.01 ? "; " + eurTxt(espera) + " aguardam a agência" : "") : eurTxt(falta) + " aguardam a agência"}</div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={() => abrirCons(o)} disabled={!(max > 0.01)} title={max > 0.01 ? "" : "O consultor só recebe depois de a agência receber a parte dele."} aria-label={"Receber a comissão de " + o.nome} style={{ ...btnPrincipal, padding: "5px 12px", opacity: max > 0.01 ? 1 : 0.45, cursor: max > 0.01 ? "pointer" : "default" }}><Wallet size={14} aria-hidden="true" />Receber</button>
        </div>
      </div>
    );
  };

  const pendentesAg = linhas.filter((o) => num(o.r) > 0 && faltaAgencia(o) > 0.01);
  const pendentesCons = linhas.filter((o) => num(o.c) > 0 && r2(num(o.c) - num(o.cRec)) > 0.01);
  const faltaAg = Math.max(0, r2(totalR - totalRec));
  const faltaCons = Math.max(0, r2(totalC - totalCRec));
  const quem = isLider ? "da equipa" : "seu";

  return (
    <div style={{ display: "grid", gap: 14, fontFamily: ds.letra, color: ds.tinta }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
        <Cartao titulo="Valor de venda" grande={num(neg.valor_venda) > 0 ? eurTxt(neg.valor_venda) : "por definir"} nota={isLider ? (referencia ? "Referência de " + String(neg.percentagem || 25).replace(".", ",") + "% sobre metade da comissão de " : "Comissão do negócio ") + (num(nc.total) > 0 ? eurTxt(nc.total) : "por definir") : null} barraVisivel={false} />
        <Cartao titulo={"Agência, reporting " + quem} grande={eurTxt(totalRec)} de={"recebidos de " + eurTxt(totalR)} nota={faltaAg > 0.01 ? "falta " + eurTxt(faltaAg) : "tudo recebido"} notaCor={faltaAg > 0.01 ? ds.atencao : ds.ok} pago={totalRec} total={totalR} />
        <Cartao titulo={"Consultor, comissão " + quem} grande={eurTxt(totalCRec)} de={"recebidos de " + eurTxt(totalC)} nota={faltaCons > 0.01 ? "falta " + eurTxt(faltaCons) : "tudo recebido"} notaCor={faltaCons > 0.01 ? ds.atencao : ds.ok} pago={totalCRec} total={totalC} />
      </div>

      {concluido && !satisfacaoEnviada && (
        <div role="status" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, background: ds.okFundo, color: ds.ok, border: "1px solid " + ds.ok, borderRadius: 12, padding: "10px 16px", fontSize: 14, fontWeight: 600 }}>
          <Check size={16} aria-hidden="true" />Negócio concluído. O formulário de satisfação ainda não foi enviado aos clientes.
          {onEnviarForm && <button onClick={() => onEnviarForm(neg)} style={{ ...btnPrincipal, marginLeft: "auto", padding: "6px 14px" }}>Enviar formulário</button>}
        </div>
      )}
      {concluido && satisfacaoEnviada && (
        <div role="status" style={{ display: "flex", alignItems: "center", gap: 8, background: ds.okFundo, color: ds.ok, borderRadius: 12, padding: "10px 16px", fontSize: 14, fontWeight: 600 }}><Check size={16} aria-hidden="true" />Negócio concluído.</div>
      )}
      {!referencia && !concluido && tudoRecebido && !escrituraFeita && (
        <div role="status" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, background: ds.atencaoFundo, color: ds.atencao, borderRadius: 12, padding: "10px 16px", fontSize: 14, fontWeight: 600 }}>
          <Landmark size={16} aria-hidden="true" />Está tudo recebido. Falta registar a escritura realizada para o negócio ficar concluído.
          {onAbrirNegocio && <button onClick={onAbrirNegocio} style={{ ...btnSecundario, marginLeft: "auto", padding: "6px 14px" }}>Abrir negócio</button>}
        </div>
      )}

      {/* 1. Recebimento da agência */}
      <div style={caixa}>
        <CabecalhoSecao titulo="Recebimento da agência" explica={referencia ? (referencia.direcao === "enviada" ? "A outra agência pagou a nossa referência" : "O cliente pagou o reporting, já sem a referência") : "O cliente pagou o reporting"}>
          {partilhado && faltaTotal > 0.01 && !tot && <button onClick={abrirTotal} style={{ ...btnSecundario, padding: "5px 12px", fontSize: 13 }}>O cliente pagou o total</button>}
        </CabecalhoSecao>
        {tot && (
          <div style={{ padding: "12px 16px", background: "#F4F7FD", borderBottom: "1px solid " + ds.linha, display: "grid", gap: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
              <label style={{ fontSize: 12.5, fontWeight: 600 }}>Total pago pelo cliente (€)<input type="number" min="0" step="0.01" aria-label="Total pago pelo cliente" value={tot.valor} onChange={(e) => mudarTot("valor", e.target.value)} style={{ ...campo, marginTop: 4 }} /></label>
              <label style={{ fontSize: 12.5, fontWeight: 600 }}>Data do pagamento<input type="date" aria-label="Data do pagamento do total" value={tot.data} onChange={(e) => mudarTot("data", e.target.value)} style={{ ...campo, marginTop: 4 }} /></label>
              <label style={{ fontSize: 12.5, fontWeight: 600 }}>Evento<select value={tot.evento} onChange={(e) => mudarTot("evento", e.target.value)} style={{ ...campo, marginTop: 4 }}>{Object.entries(NOME_EVENTO).map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></label>
              <label style={{ fontSize: 12.5, fontWeight: 600 }}>Mês do reporting<input type="month" value={tot.mes} onChange={(e) => mudarTot("mes", e.target.value)} style={{ ...campo, marginTop: 4 }} /></label>
              <label style={{ fontSize: 12.5, fontWeight: 600 }}>Nº fatura<input type="text" value={tot.fatura} onChange={(e) => mudarTot("fatura", e.target.value)} style={{ ...campo, marginTop: 4 }} /></label>
              <label style={{ fontSize: 12.5, fontWeight: 600 }}>Nº recibo<input type="text" value={tot.recibo} onChange={(e) => mudarTot("recibo", e.target.value)} style={{ ...campo, marginTop: 4 }} /></label>
            </div>
            {num(tot.valor) > 0 && <div style={{ fontSize: 13.5, color: ds.tinta2 }}>Fica repartido conforme o que falta a cada um: {linhas.filter((o) => num(parteTotal[o.k]) > 0).map((o) => o.nome + " " + eurTxt(parteTotal[o.k])).join(", ") || "nada a repartir"}.</div>}
            {tot.erro && <div role="alert" style={erroTxt}>{tot.erro}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setTot(null)} style={btnSecundario}>Cancelar</button>
              <button onClick={confirmarTotal} disabled={aGravar} style={{ ...btnPrincipal, opacity: aGravar ? 0.7 : 1 }}>{aGravar ? "A gravar..." : "Confirmar e repartir"}</button>
            </div>
          </div>
        )}
        <div id="historico-recebimentos-anchor" style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 1000 }}>
            <CabTabela grid={G_AG} cab={CAB_AG} />
            {histAg.map((r) => {
              const anulada = r.nota_credito_estado === "emitida";
              return (
                <div key={r.id} style={{ ...linhaBase, gridTemplateColumns: G_AG, opacity: anulada ? 0.55 : 1 }}>
                  <div style={{ fontWeight: 600 }}>{dataPT(r.data_pago_cliente || r.data_evento) || "sem data"}</div>
                  <div>{chip(nomeEvento(r.tipo_evento))}</div>
                  <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.consultor_nome || ""}>{r.consultor_nome || "Sem consultor"}</div>
                  <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{eurTxt(r.valor_cliente)}</div>
                  <div>{celMes(r)}</div>
                  <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.numero_fatura || ""}>{r.numero_fatura || <span style={{ color: ds.tinta3 }}>sem fatura</span>}</div>
                  <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.numero_recibo || ""}>{r.numero_recibo || <span style={{ color: ds.tinta3 }}>sem recibo</span>}</div>
                  <div style={{ display: "grid", gap: 2 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                      {r.nota_credito_estado === "pedida"
                        ? (isLider ? <button onClick={() => marcarNC(r)} title="Marcar a nota de crédito como emitida (o valor deixa de contar como recebido)" style={{ background: ds.atencaoFundo, color: ds.atencao, border: "1px solid " + ds.atencao, borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>NC pedida, marcar emitida</button> : <span style={{ color: ds.atencao }}>NC pedida</span>)
                        : anulada ? <span style={{ color: ds.tinta2 }}>NC emitida</span> : <span style={{ color: ds.ok }}>Recebido</span>}
                    </span>
                    {celConfirmado(r)}
                  </div>
                  {acoes(r, num(r.valor_consultor) > 0)}
                </div>
              );
            })}
            {pendentesAg.map(linhaPendenteAg)}
            {!histAg.length && !pendentesAg.length && <div style={{ padding: "14px 16px", fontSize: 14, color: ds.tinta2 }}>Sem valores de reporting definidos para registar.</div>}
          </div>
        </div>
      </div>

      {/* 2. Recebimento do consultor */}
      <div style={caixa}>
        <CabecalhoSecao titulo="Recebimento do consultor" explica="O consultor recebeu a sua percentagem" />
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 760 }}>
            <CabTabela grid={G_CONS} cab={CAB_CONS} />
            {histCons.map((r) => (
              <div key={r.id} style={{ ...linhaBase, gridTemplateColumns: G_CONS }}>
                <div style={{ fontWeight: 600 }}>{dataPT(r.data_pago_consultor || r.data_evento) || "sem data"}</div>
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.consultor_nome || ""}>{r.consultor_nome || "Sem consultor"}</div>
                <div style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{eurTxt(r.valor_consultor)}</div>
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.numero_fatura || ""}>{r.numero_fatura || <span style={{ color: ds.tinta3 }}>sem fatura/recibo</span>}</div>
                <div>{celConfirmado(r)}</div>
                {acoes(r, num(r.valor_cliente) > 0)}
              </div>
            ))}
            {pendentesCons.map(linhaPendenteCons)}
            {!histCons.length && !pendentesCons.length && <div style={{ padding: "14px 16px", fontSize: 14, color: ds.tinta2 }}>Sem comissão definida para registar.</div>}
          </div>
        </div>
      </div>

      {avisoConcluido && <JanelaConcluido neg={neg} onFechar={() => setAvisoConcluido(false)} onEnviar={() => { setAvisoConcluido(false); if (onEnviarForm) onEnviarForm(neg); }} />}
    </div>
  );
}
